import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { BodyText, ButtonGroup, theme, useResponsive } from '@sg/garnish';
import { DeliveryPreferenceType } from '@sg/graphql-schema';

export const DeliveryPreferencesFormTypeSelector = (
  props: DeliveryPreferencesFormTypeSelectorProps,
) => {
  const { localType, setLocalType } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();
  const itemsHorizontalPadding = match(['3', '4'] as const);

  const deliveryPreferences = useMemo(
    () => [
      {
        value: DeliveryPreferenceType.LeaveAtDoor,
        label: formatMessage(messages.addressesDeliveryPreferenceLeaveAtDoor),
        accessibilityLabel: formatMessage(messages.leaveAtDoorA11y),
      },
      {
        value: DeliveryPreferenceType.Door,
        label: formatMessage(messages.addressesDeliveryPreferenceMeetAtDoor),
        accessibilityLabel: formatMessage(messages.meetAtDoorA11y),
      },
      {
        value: DeliveryPreferenceType.Curbside,
        label: formatMessage(messages.addressesDeliveryPreferenceMeetOutside),
        accessibilityLabel: formatMessage(messages.meetOutsideA11y),
      },
    ],
    [formatMessage],
  );

  return (
    <View>
      <BodyText sizeMatch={['12']} style={styles.sectionLabel}>
        {formatMessage(messages.deliveryPreferencesDropoffLabel)}
      </BodyText>

      <ButtonGroup
        value={localType}
        items={deliveryPreferences}
        itemsHorizontalPadding={itemsHorizontalPadding}
        onChange={setLocalType}
      />
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addressesDeliveryPreferenceLeaveAtDoor: {
    defaultMessage: 'Leave at door',
    description: 'Addresses | Delivery Preference | Leave At Door',
  },
  addressesDeliveryPreferenceMeetAtDoor: {
    defaultMessage: 'Meet at door',
    description: 'Addresses | Delivery Preference | Meet At Door',
  },
  addressesDeliveryPreferenceMeetOutside: {
    defaultMessage: 'Meet outside',
    description: 'Addresses | Delivery Preference | Meet Outside',
  },
  leaveAtDoorA11y: {
    defaultMessage: 'Drop-off option, leave at door',
    description: 'Addresses | Delivery Preference | Leave At Door | A11y',
  },
  meetAtDoorA11y: {
    defaultMessage: 'Drop-off option, meet at door',
    description: 'Addresses | Delivery Preference | Meet At Door | A11y',
  },
  meetOutsideA11y: {
    defaultMessage: 'Drop-off option, meet outside',
    description: 'Addresses | Delivery Preference | Meet Outside | A11y',
  },
  deliveryPreferencesDropoffLabel: {
    defaultMessage: 'Drop-Off',
    description: 'Delivery | Preferences | Dropoff Label',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type DeliveryPreferencesFormTypeSelectorProps = Readonly<{
  localType: string;
  setLocalType: (type: string) => void;
}>;

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  sectionLabel: {
    marginTop: theme.spacing['10'],
    marginBottom: theme.spacing['2'],
  },
});
